import { isItemDestroyed, weaponInstanceKey, type Combatant, type DisplayCombatant, type HpHiddenCombatant, type WeaponProfile } from "@lancer-vtt/domain";
import { isMaskedCombatant } from "./disclosure";
import { UnitSheetStatuses, type UnitSheetIdentity } from "./combatWindows/UnitSheet";
import { descriptiveStatCells, maskedStatCells, StatStrip, STAT_MASKED } from "./combatWindows/unitStats";
import { MaskedSheetVitals, npcVitalTracks, UnitSheetVitals } from "./combatWindows/unitVitals";
import { formatWeaponDamage, formatWeaponRange } from "./weaponFormat";
import { faUpRightAndDownLeftFromCenter } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../Icon";
import "./unit-intel-card.css";

/** The footer a player reads when anything on the card is masked: what would reveal it. */
export const SCAN_HINT = `${STAT_MASKED} — Scan (Quick Tech) to reveal`;

/**
 * **The unit panel's card** (`projects/hover-intel.md` Phase 2, `projects/unit-panel.md` Phase 1):
 * what the selected or hovered unit is, read at a glance while the board paints its reach. It sits
 * at the bottom of the unit column (`UnitColumn.tsx`), under the command menu of a unit the viewer
 * controls.
 *
 * `preview` marks a hovered unit shown in place of the selection: a dashed frame and a `HOVER` tag,
 * so it never reads as the menu's unit, and it lets the pointer through to the board.
 * `onExpand` puts the expand
 * control in the header, which grows the panel into the full sheet (`UnitPanel.tsx`, Phase 2).
 *
 * It takes the branded {@link DisplayCombatant} and reuses the sheet's masked renderings rather than
 * a second masking path: `"class"` and `"loadout"` hostiles get {@link MaskedSheetVitals} and
 * {@link maskedStatCells} (`??` but for SIZE); at `"class"` the weapons are the ones revealed by use
 * plus a count of the rest off `redactedCounts`; a `"full"` hostile with `showEnemyHp` off reads HP `?? / max`. The GM's
 * projection is the real combatant, so the GM sees everything and never the hint.
 *
 * The Scan hint shows exactly when the projection masked the unit. HP hidden by the `showEnemyHp`
 * policy alone gets none, because a Scan does not reveal live HP (`decisions/0082`).
 */
export function UnitIntelCard({
  combatant: c,
  identity,
  preview = false,
  onExpand,
}: {
  combatant: DisplayCombatant;
  identity: UnitSheetIdentity;
  preview?: boolean;
  onExpand?: (() => void) | undefined;
}) {
  const masked = isMaskedCombatant(c);
  const kindLine = [...identity.kindLine, ...(identity.tier !== undefined ? [`T${identity.tier}`] : []), ...identity.templates.map((t) => t.name)];
  return (
    <aside
      className={`uic${identity.side === "npc" && !identity.tag ? " is-hostile" : ""}${preview ? " is-preview" : ""}`}
      data-testid="unit-intel-card"
      data-unit-id={c.id}
      aria-label={`${identity.name} intel`}
    >
      <header className="uic-head">
        <span className="uic-name-line">
          <span className="uic-name">{identity.name}</span>
          {preview && <span className="uic-tag">HOVER</span>}
          {onExpand && (
            <button type="button" className="uic-expand" aria-expanded={false} aria-label={`Expand ${identity.name}'s sheet`} title="Full sheet" onClick={onExpand}>
              <Icon icon={faUpRightAndDownLeftFromCenter} />
            </button>
          )}
        </span>
        {kindLine.length > 0 && <span className="uic-kind">{kindLine.join(" · ")}</span>}
      </header>
      {masked ? <MaskedSheetVitals /> : <UnitSheetVitals {...intelVitalTracks(c)} onSetTrack={null} disabled />}
      <StatStrip cells={masked ? maskedStatCells(c.base) : descriptiveStatCells(c.base, { grit: false })} className={masked ? "is-masked" : undefined} onOpenDice={null} />
      <IntelWeapons c={c} />
      <UnitSheetStatuses combatant={c} edit={null} />
      {masked && <footer className="uic-hint">{SCAN_HINT}</footer>}
    </aside>
  );
}

/** The vitals of an unmasked unit, read-only. A unit whose base has no structure track shows none. */
function intelVitalTracks(c: Combatant | HpHiddenCombatant) {
  const tracks = npcVitalTracks(c);
  return c.base.maxStructure === undefined ? { ...tracks, structure: undefined } : tracks;
}

/**
 * The weapon list: each known weapon's name, range and damage. At `"class"` the known weapons are
 * the ones the unit revealed by using them (`decisions/0207`), and the header adds how many are
 * still locked (`WEAPONS · 1 · +1 ▒▒`); with none revealed it is only the count.
 */
function IntelWeapons({ c }: { c: DisplayCombatant }) {
  const weapons: WeaponProfile[] = c.base.weapons ?? [];
  const locked = "redactedCounts" in c ? c.redactedCounts.weapons : 0;
  if ("redactedCounts" in c && weapons.length === 0) {
    return (
      <div className="uic-weapons">
        <div className="uic-section">
          WEAPONS · {locked} · {STAT_MASKED}
        </div>
      </div>
    );
  }
  if (weapons.length === 0) return null;
  const resources = isMaskedCombatant(c) ? undefined : c.current.resources;
  return (
    <div className="uic-weapons">
      <div className="uic-section">
        WEAPONS · {weapons.length}
        {locked > 0 && ` · +${locked} ${STAT_MASKED}`}
      </div>
      <ul className="uic-weapon-list">
        {weapons.map((w) => {
          const destroyed = isItemDestroyed(resources, weaponInstanceKey(w));
          return (
            <li key={weaponInstanceKey(w)} className={`uic-weapon${destroyed ? " is-destroyed" : ""}`}>
              <span className="uic-weapon-name">{w.name}</span>
              <span className="uic-weapon-range">{destroyed ? "DESTROYED" : formatWeaponRange(w.range ?? []) || "—"}</span>
              <span className="uic-weapon-dmg">{formatWeaponDamage(w.damage)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
